import { FC, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Typography,
  Badge,
  Card,
  CardContent,
  toast,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconRestore, IconEye, IconX } from "@tabler/icons-react";
import { LocalAgentVersion } from "../hooks/useLocalAgents";
import { LocalVersionComparisonDialog } from "./LocalVersionComparisonDialog";

interface LocalAgentVersionHistoryProps {
  agentName: string;
  currentVersion: number;
  versions: LocalAgentVersion[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (version: number) => void;
}

export const LocalAgentVersionHistory: FC<LocalAgentVersionHistoryProps> = ({
  agentName,
  currentVersion,
  versions,
  open,
  onOpenChange,
  onRestore,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  const handleRestore = (version: number) => {
    if (window.confirm(`Are you sure you want to restore to version ${version}? This will create a new version with the content from v${version}.`)) {
      onRestore(version);
      toast({
        title: "Success",
        description: `Agent restored to version ${version} (new version created)`,
      });
      onOpenChange(false);
    }
  };

  const handleCompare = (version: number) => {
    setSelectedVersion(version);
    setShowComparison(true);
  };

  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="al-max-w-4xl al-max-h-[90vh] al-overflow-hidden al-flex al-flex-col">
          <DialogHeader>
            <Stack direction="row" justify="between" align="center">
              <div>
                <DialogTitle className="al-text-xl">Version History</DialogTitle>
                <Typography className="al-text-gray-500 al-mt-1">
                  {agentName} - {versions.length} version{versions.length !== 1 ? 's' : ''}
                </Typography>
              </div>
              <Button
                variant="ghost"
                size="icon"
                title="Close"
                onClick={() => onOpenChange(false)}
              >
                <IconX className="al-w-4 al-h-4" />
              </Button>
            </Stack>
          </DialogHeader>

          <div className="al-overflow-y-auto al-flex-1 al-space-y-3">
            {sortedVersions.map((version) => {
              const isCurrent = version.version === currentVersion;

              return (
                <Card key={version.version} className={isCurrent ? "al-border-2 al-border-blue-500" : ""}>
                  <CardContent className="al-p-4">
                    <Stack direction="row" justify="between" align="start">
                      <div className="al-flex-1">
                        <Stack direction="row" gap="sm" align="center" className="al-mb-2">
                          <Badge className={isCurrent ? "!al-bg-blue-600 !al-text-white" : "!al-bg-gray-600 !al-text-white"}>
                            v{version.version}
                          </Badge>
                          {isCurrent && (
                            <Badge className="!al-bg-green-100 !al-text-green-700">
                              Current
                            </Badge>
                          )}
                          <Typography className="al-text-gray-500">
                            {new Date(version.created_at).toLocaleString()}
                          </Typography>
                        </Stack>

                        <Typography variant="body" weight="medium" className="al-mb-1">
                          {version.name}
                        </Typography>
                        <Typography className="al-text-gray-600 al-mb-2">
                          {version.description}
                        </Typography>
                        <Typography className="al-font-mono al-text-blue-700">
                          Model: {version.model}
                        </Typography>
                      </div>

                      <Stack direction="row" gap="sm">
                        <Button
                          onClick={() => handleCompare(version.version)}
                          variant="outline" size="sm"
                          disabled={isCurrent}
                        >
                          <IconEye className="al-w-4 al-h-4 al-mr-2" />
                          Compare
                        </Button>
                        {!isCurrent && (
                          <Button
                            onClick={() => handleRestore(version.version)}
                            variant="outline" size="sm"
                          >
                            <IconRestore className="al-w-4 al-h-4 al-mr-2" />
                            Restore
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}

            {versions.length === 0 && (
              <div className="al-text-center al-py-8">
                <Typography className="al-text-gray-500">
                  No version history available
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Version Comparison Dialog */}
      {selectedVersion && (
        <LocalVersionComparisonDialog
          agentName={agentName}
          version1={currentVersion}
          version2={selectedVersion}
          versions={versions}
          open={showComparison}
          onOpenChange={setShowComparison}
        />
      )}
    </>
  );
};
